// Formatting through Intl only (DESIGN-RULES 6 and 11). The locale and preferences come from setFormatPrefs().

type Prefs = { locale: string; timeZone?: string; duration: "decimal" | "hm"; hour12?: boolean };
let prefs: Prefs = { locale: typeof navigator === "undefined" ? "en" : navigator.language || "en", duration: "decimal" };
export function setFormatPrefs(next: Partial<Prefs>) {
  prefs = { ...prefs, ...next };
  cache.clear();
}
export const getFormatPrefs = () => prefs;

const cache = new Map<string, Intl.NumberFormat | Intl.DateTimeFormat | Intl.RelativeTimeFormat>();
function nf(key: string, opts: Intl.NumberFormatOptions) {
  const k = `n|${key}`;
  if (!cache.has(k)) cache.set(k, new Intl.NumberFormat(prefs.locale, opts));
  return cache.get(k) as Intl.NumberFormat;
}
function df(key: string, opts: Intl.DateTimeFormatOptions) {
  const k = `d|${key}`;
  if (!cache.has(k)) cache.set(k, new Intl.DateTimeFormat(prefs.locale, { timeZone: prefs.timeZone, hour12: prefs.hour12, ...opts }));
  return cache.get(k) as Intl.DateTimeFormat;
}

/** Money always carries its currency. Whole amounts drop the cents ("$18,400"); others keep two places. */
export function money(amount: number, currency: string, opts: { compact?: boolean; cents?: boolean } = {}) {
  const whole = Number.isInteger(amount) && !opts.cents;
  const key = `${currency}|${opts.compact}|${whole}`;
  return nf(key, {
    style: "currency",
    currency,
    notation: opts.compact ? "compact" : "standard",
    minimumFractionDigits: whole || opts.compact ? 0 : 2,
    maximumFractionDigits: opts.compact ? 1 : whole ? 0 : 2,
  }).format(amount);
}

export const number = (n: number, digits = 0) => nf(`num|${digits}`, { maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(n);
export const percent = (fraction: number, digits = 0) => nf(`pct|${digits}`, { style: "percent", maximumFractionDigits: digits }).format(fraction);
export const compact = (n: number) => nf("compact", { notation: "compact", maximumFractionDigits: 1 }).format(n);

const DAY = 86_400_000;
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

/** "Today, 17:00", "Tomorrow", "Oct 22", "Oct 22, 2025" (DESIGN-RULES 11). */
export function date(value: Date | string | number, opts: { time?: boolean; now?: Date } = {}) {
  // a date-only string is a calendar day: read it in local time (new Date("2026-11-02") would be UTC midnight, the day before
  // for anyone west of UTC)
  const ymd = typeof value === "string" ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(value) : null;
  const d = ymd ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3])) : new Date(value);
  const now = opts.now ?? new Date();
  const diff = Math.round((startOfDay(d) - startOfDay(now)) / DAY);
  const time = opts.time ? df("time", { hour: "numeric", minute: "2-digit" }).format(d) : "";
  if (diff === 0 || diff === 1 || diff === -1) {
    const word = (cache.get("rtf") as Intl.RelativeTimeFormat | undefined) ?? new Intl.RelativeTimeFormat(prefs.locale, { numeric: "auto" });
    cache.set("rtf", word);
    const label = word.format(diff, "day");
    const cap = label.charAt(0).toLocaleUpperCase(prefs.locale) + label.slice(1);
    return time ? `${cap}, ${time}` : cap;
  }
  const sameYear = d.getFullYear() === now.getFullYear();
  const day = df(sameYear ? "md" : "mdy", sameYear ? { month: "short", day: "numeric" } : { month: "short", day: "numeric", year: "numeric" }).format(d);
  return time ? `${day}, ${time}` : day;
}

/** Days from today to the date (negative when past). */
export const daysFromNow = (value: Date | string | number, now = new Date()) => Math.round((startOfDay(new Date(value)) - startOfDay(now)) / DAY);

/** "12m", "2h", "3d" for the last 7 days, then a date (activity feeds). */
export function relative(value: Date | string | number, now = new Date()) {
  const ms = now.getTime() - new Date(value).getTime();
  const min = Math.round(ms / 60_000);
  if (min < 1) return nf("unit-s", { style: "unit", unit: "second", unitDisplay: "narrow" }).format(Math.max(1, Math.round(ms / 1000)));
  if (min < 60) return nf("unit-m", { style: "unit", unit: "minute", unitDisplay: "narrow" }).format(min);
  const h = Math.round(min / 60);
  if (h < 24) return nf("unit-h", { style: "unit", unit: "hour", unitDisplay: "narrow" }).format(h);
  const d = Math.round(h / 24);
  if (d <= 7) return nf("unit-d", { style: "unit", unit: "day", unitDisplay: "narrow" }).format(d);
  return date(value, { now });
}

/** Hours as "6.5h" or "6h 30m", by the person's setting. */
export function duration(hours: number, style: Prefs["duration"] = prefs.duration) {
  if (style === "decimal") return `${number(hours, hours % 1 ? 2 : 0).replace(/(\.\d)0$/, "$1")}h`;
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  return m ? (h ? `${h}h ${m}m` : `${m}m`) : `${h}h`;
}

/** Seconds as a running clock "01:24:10" (timers, in the mono face). */
export function clock(totalSeconds: number) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
}

export function fileSize(bytes: number) {
  const units = ["byte", "kilobyte", "megabyte", "gigabyte", "terabyte"] as const;
  let i = 0;
  let v = bytes;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return nf(`fs|${i}`, { style: "unit", unit: units[i], unitDisplay: "short", maximumFractionDigits: i ? 1 : 0 }).format(v);
}

/** Initials for avatars and client chips: "Lumen Dental" -> "LD". */
export const initials = (name: string) => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toLocaleUpperCase()).join("");
